import { useEffect, useState } from 'react';
import type { ChangeEventHandler, InputHTMLAttributes } from 'react';

const PASSWORD_AUTO_HIDE_DELAY_MS = 5_000;

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> & {
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  toggleTestId?: string;
};

export default function PasswordInput({
  value,
  onChange,
  disabled,
  toggleTestId,
  ...inputProps
}: PasswordInputProps) {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const toggleLabel = isPasswordVisible ? 'Hide password' : 'Show password';

  useEffect(() => {
    if (!isPasswordVisible) return;

    const autoHideTimeout = window.setTimeout(() => {
      setIsPasswordVisible(false);
    }, PASSWORD_AUTO_HIDE_DELAY_MS);

    return () => window.clearTimeout(autoHideTimeout);
  }, [isPasswordVisible]);

  return (
    <div className="password-input">
      <input
        {...inputProps}
        type={isPasswordVisible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      <button
        type="button"
        className="subtle-btn password-input-toggle"
        aria-label={toggleLabel}
        aria-pressed={isPasswordVisible}
        title={toggleLabel}
        onClick={() => setIsPasswordVisible((visible) => !visible)}
        disabled={disabled}
        data-testid={toggleTestId}
      >
        {isPasswordVisible ? (
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3l18 18" />
            <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
            <path d="M9.9 4.2A10.9 10.9 0 0 1 12 4c7 0 10 8 10 8a18.2 18.2 0 0 1-3.1 4.2" />
            <path d="M6.6 6.6A18.5 18.5 0 0 0 2 12s3 8 10 8a10.8 10.8 0 0 0 4.2-.8" />
          </svg>
        ) : (
          <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8S2 12 2 12Z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
}
